import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/**
 * Confirmación con la piel del sistema, para reemplazar `window.confirm`.
 *
 * Un diálogo nativo del navegador no toma el tema —en modo oscuro aparece un
 * cuadro blanco del sistema—, no respeta la tipografía ni el radio, y varios
 * navegadores permiten silenciarlo para un sitio: cuando eso pasa, `confirm()`
 * devuelve `false` sin preguntar y la acción queda bloqueada para siempre sin
 * que el usuario entienda por qué.
 *
 * Se monta UNA vez en el shell y se usa desde cualquier componente:
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title: '¿Eliminar el contacto?', destructive: true })) {
 *     …
 *   }
 *
 * El sitio de llamada cambia una línea. La alternativa —un AlertDialog local
 * en cada uno— deja el mismo estado duplicado en cada componente.
 *
 * La acción destructiva es la SECUNDARIA y el foco arranca en cancelar, al
 * revés que `window.confirm`, donde "Aceptar" toma el foco y un Enter
 * distraído borra un contacto.
 */

export interface ConfirmOptions {
  title: ReactNode;
  description?: ReactNode;
  /** Default: "Confirmar", o "Eliminar" cuando `destructive`. */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Pinta la acción en tono destructivo. Default: false. */
  destructive?: boolean;
}

type Confirm = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Confirm | null>(null);

/**
 * Devuelve la función de confirmación. Lanza si no hay proveedor montado: es
 * preferible a devolver `true` en silencio y ejecutar un borrado sin preguntar.
 */
export function useConfirm(): Confirm {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error(
      'useConfirm() necesita <ConfirmProvider>, que se monta en AppLayout.',
    );
  }
  return ctx;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<Confirm>((opts) => {
    setOptions(opts);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
    setOptions(null);
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <AlertDialog
        open={options !== null}
        onOpenChange={(open) => {
          // Cerrar por escape, por clic afuera o por el botón: todo es cancelar.
          if (!open) settle(false);
        }}
      >
        {options && (
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{options.title}</AlertDialogTitle>
              {options.description && (
                <AlertDialogDescription>
                  {options.description}
                </AlertDialogDescription>
              )}
            </AlertDialogHeader>
            <AlertDialogFooter>
              {/* Cancelar va primero y se lleva el foco por defecto. */}
              <AlertDialogCancel onClick={() => settle(false)}>
                {options.cancelLabel ?? 'Cancelar'}
              </AlertDialogCancel>
              <AlertDialogAction
                variant={options.destructive ? 'destructive' : 'default'}
                onClick={() => settle(true)}
              >
                {options.confirmLabel ??
                  (options.destructive ? 'Eliminar' : 'Confirmar')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}
